import type { StaffCatalogListItem } from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import { Badge } from "../../ui/badge";
import { DataTable } from "../../ui/data-table";
import { TableLink } from "../../ui/links";
import { StatusBadge } from "../../ui/status-badge";
import { ItemStatusBadge } from "../components/ItemStatusBadge";
import {
  formatDate,
  itemTypeLabels,
  priceClassLabels,
  pricingModelLabels,
  verificationFreshness,
} from "../labels";

type CatalogTableProps = Readonly<{
  items: readonly StaffCatalogListItem[];
  now: Date;
  caption: string;
}>;

const column = createColumnHelper<StaffCatalogListItem>();

const freshnessWords = { fresh: "Fresh", stale: "Stale", never: "Never" } as const;

/**
 * The catalog list as a `DataTable`. It has a caption and column headers with `scope`. The name is a link to the item. Under 720 px of
 * table width each row becomes a card (every cell keeps its header word), so there is no sideways scroll on a phone. The server orders the
 * rows (keyset paging), so the headers do not sort: a sort here would only reorder the pages already loaded.
 */
export function CatalogTable({ items, now, caption }: CatalogTableProps) {
  const columns = useMemo(
    () => [
      column.accessor("name", {
        header: "Name",
        meta: { rowHeader: true },
        cell: (info) => (
          <span className="flex flex-wrap items-center gap-x-2">
            <TableLink
              to={`/catalog/items/${encodeURIComponent(info.row.original.id)}`}
              className="font-serif text-lg font-medium text-foreground"
            >
              {info.getValue()}
            </TableLink>
            {info.row.original.editorialPriority > 0 ? <Badge tone="brass">Featured</Badge> : null}
          </span>
        ),
      }),
      column.accessor((item) => itemTypeLabels[item.itemType], {
        id: "type",
        header: "Type",
      }),
      column.accessor("status", {
        header: "Status",
        cell: (info) => <ItemStatusBadge status={info.getValue()} />,
      }),
      column.accessor((item) => [item.locality, item.countryCode].filter(Boolean).join(", "), {
        id: "place",
        header: "Place",
        cell: (info) => info.getValue() || "Not set",
      }),
      column.display({
        id: "price",
        header: "Price",
        cell: (info) => (
          <span className="grid">
            <span>{pricingModelLabels[info.row.original.pricingModel]}</span>
            <span className="text-[13px] text-muted-foreground">
              {priceClassLabels[info.row.original.priceClass]}
            </span>
          </span>
        ),
      }),
      column.display({
        id: "verified",
        header: "Verified",
        cell: (info) => {
          const item = info.row.original;
          const freshness = verificationFreshness(item.lastVerifiedAt, now);
          return (
            <span className="flex flex-col items-start gap-0.5">
              <StatusBadge tone={freshness === "fresh" ? "success" : "warning"}>
                {freshnessWords[freshness]}
              </StatusBadge>
              {item.lastVerifiedAt === null ? null : (
                <span className="text-[13px] text-muted-foreground">
                  {formatDate(item.lastVerifiedAt)}
                </span>
              )}
            </span>
          );
        },
      }),
      column.accessor((item) => (item.hasCover ? "Yes" : "No cover"), {
        id: "cover",
        header: "Cover",
        meta: { className: "whitespace-nowrap" },
      }),
      column.accessor((item) => formatDate(item.updatedAt), {
        id: "updated",
        header: "Updated",
        meta: { className: "whitespace-nowrap" },
      }),
    ],
    [now],
  );

  return (
    <DataTable
      columns={columns}
      data={items}
      getRowId={(item) => item.id}
      caption={caption}
      banded
    />
  );
}
